/**
 * Time-to-do chips for queues and sheets (prototype spec §4.5, §9.5, §9.6).
 * Two kinds: `due` (when something must be done) and `age` (how long
 * something has waited). The word is always in the chip, never colour alone.
 */
import type { Tone } from '@/lib/status';

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

/** "2 d 6 h", "5 h", "40 min", "under 1 min". */
export function duration(ms: number): string {
  const span = Math.max(0, ms);
  if (span < MINUTE) return 'under 1 min';
  if (span < HOUR) return `${Math.floor(span / MINUTE)} min`;
  if (span < DAY) return `${Math.floor(span / HOUR)} h`;
  const days = Math.floor(span / DAY);
  return `${days} d ${Math.floor((span % DAY) / HOUR)} h`;
}

export interface SlaState {
  tone: Tone;
  label: string;
}

/**
 * When something is due. Overdue is danger, due within `warnWithinMs` is
 * warning, later is neutral. A resolved item is quiet and says how long it took.
 */
export function dueState(
  dueAt: string,
  now: number,
  options: {
    resolvedAt?: string | null;
    startedAt?: string | null;
    warnWithinMs?: number;
  } = {},
): SlaState | null {
  const due = Date.parse(dueAt);
  if (!Number.isFinite(due)) return null;
  if (options.resolvedAt) {
    const done = Date.parse(options.resolvedAt);
    const started = options.startedAt
      ? Date.parse(options.startedAt)
      : Number.NaN;
    if (Number.isFinite(done) && Number.isFinite(started)) {
      return {
        tone: 'success',
        label: `Resolved in ${duration(done - started)}`,
      };
    }
    return { tone: 'success', label: 'Resolved' };
  }
  const left = due - now;
  if (left <= 0) {
    return { tone: 'danger', label: `Overdue ${duration(-left)}` };
  }
  const warn = options.warnWithinMs ?? 6 * HOUR;
  return {
    tone: left <= warn ? 'warning' : 'neutral',
    label: `Due in ${duration(left)}`,
  };
}

/** Neutral under 24 h, amber from 24 h, red over 72 h (§9.5). */
export function ageTone(ms: number): Tone {
  if (ms > 72 * HOUR) return 'danger';
  if (ms >= 24 * HOUR) return 'warning';
  return 'neutral';
}

/** How long something has waited since `startedAt`. */
export function ageState(startedAt: string, now: number): SlaState | null {
  const started = Date.parse(startedAt);
  if (!Number.isFinite(started)) return null;
  const age = Math.max(0, now - started);
  return { tone: ageTone(age), label: `Waiting ${duration(age)}` };
}
